import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tv, Star, Youtube, Plus, Trash2, Edit3, Eye, EyeOff, Search, ExternalLink, Film, Check, X, Globe, Calendar, MapPin } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function MediaShowcaseAdminPage() {
  const [mediaItems, setMediaItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", category: "evenement", event_id: "", youtube_urls: [""], is_featured: false, glotech_production: false, status: "draft" });

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [mediaRes, eventsRes] = await Promise.all([
        axios.get(`${API}/api/showcase/media`, { params: { limit: 100 } }),
        axios.get(`${API}/api/event/events`, { headers }),
      ]);
      // Include all media (published + draft) for admin - fetch from admin endpoint
      const allMedia = await axios.get(`${API}/api/showcase/admin/media-all`, { headers }).catch(() => ({ data: mediaRes.data }));
      setMediaItems(allMedia.data || mediaRes.data);
      setEvents(eventsRes.data || []);
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditItem(null);
    setForm({ title: "", description: "", category: "evenement", event_id: "", youtube_urls: [""], is_featured: false, glotech_production: false, status: "draft" });
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      title: item.title || "",
      description: item.description || "",
      category: item.category || "evenement",
      event_id: item.event_id || "",
      youtube_urls: item.youtube_urls?.length ? item.youtube_urls : [""],
      is_featured: item.is_featured || false,
      glotech_production: item.glotech_production || false,
      status: item.status || "draft",
    });
    setShowForm(true);
  };

  const saveItem = async () => {
    if (!form.title) { toast.error("Titre obligatoire"); return; }
    const data = { ...form, youtube_urls: form.youtube_urls.filter(u => u.trim()) };
    try {
      if (editItem) {
        await axios.put(`${API}/api/showcase/media/${editItem.id}`, data, { headers });
        toast.success("Media mis a jour");
      } else {
        await axios.post(`${API}/api/showcase/media`, data, { headers });
        toast.success("Media cree");
      }
      setShowForm(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Supprimer ce media ?")) return;
    try {
      await axios.delete(`${API}/api/showcase/media/${id}`, { headers });
      toast.success("Supprime");
      fetchData();
    } catch { toast.error("Erreur suppression"); }
  };

  const toggleFeatured = async (item) => {
    try {
      await axios.put(`${API}/api/showcase/media/${item.id}`, { ...item, is_featured: !item.is_featured }, { headers });
      toast.success(item.is_featured ? "Retire de la une" : "Mis en vedette");
      fetchData();
    } catch { toast.error("Erreur"); }
  };

  const togglePublish = async (item) => {
    const newStatus = item.status === "published" ? "draft" : "published";
    try {
      await axios.put(`${API}/api/showcase/media/${item.id}`, { ...item, status: newStatus }, { headers });
      toast.success(newStatus === "published" ? "Publie" : "Depublie");
      fetchData();
    } catch { toast.error("Erreur"); }
  };

  const addYoutubeField = () => setForm(f => ({ ...f, youtube_urls: [...f.youtube_urls, ""] }));
  const updateYoutube = (i, val) => setForm(f => ({ ...f, youtube_urls: f.youtube_urls.map((u, j) => j === i ? val : u) }));
  const removeYoutube = (i) => setForm(f => ({ ...f, youtube_urls: f.youtube_urls.filter((_, j) => j !== i) }));

  const catLabel = { conference: "Conference", reportage: "Reportage", live: "Live", evenement: "Evenement" };
  const eventMap = {};
  events.forEach(e => { eventMap[e.id] = e.title; });

  return (
    <div className="min-h-screen bg-[#0B0F19] text-white p-4 sm:p-6" data-testid="media-admin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center"><Tv className="h-5 w-5 text-purple-400" /></div>
            Vitrine Evenements & Medias
          </h1>
          <p className="text-gray-500 text-sm mt-1">Gerez les evenements en vedette, liens YouTube et contenus mediatiques</p>
        </div>
        <Button onClick={openCreate} className="bg-[#0099CC] hover:bg-[#00AADD] text-white" data-testid="create-media-btn">
          <Plus className="h-4 w-4 mr-2" /> Nouveau media
        </Button>
      </div>

      {/* Media list */}
      {loading ? (
        <div className="text-center text-gray-500 py-12">Chargement...</div>
      ) : mediaItems.length === 0 ? (
        <div className="text-center py-16 bg-[#131B2E] rounded-xl border border-[#2A3441]">
          <Film className="h-12 w-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500">Aucun media. Creez votre premier contenu pour la vitrine publique.</p>
          <Button onClick={openCreate} className="mt-4 bg-[#0099CC]"><Plus className="h-4 w-4 mr-1" /> Creer</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {mediaItems.map(item => (
            <div key={item.id} className={`bg-[#131B2E] border rounded-xl overflow-hidden transition-all ${item.is_featured ? "border-amber-500/50 ring-1 ring-amber-500/20" : "border-[#2A3441]"}`} data-testid={`media-card-${item.id}`}>
              {/* Card Header */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${item.status === "published" ? "bg-emerald-500/20 text-emerald-400" : "bg-gray-600/20 text-gray-400"}`}>
                        {item.status === "published" ? "Publie" : "Brouillon"}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300">{catLabel[item.category] || item.category}</span>
                      {item.is_featured && <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />}
                    </div>
                    <h3 className="font-semibold text-sm truncate">{item.title}</h3>
                    {item.event_id && <p className="text-xs text-gray-500 mt-0.5 truncate">Lie a : {eventMap[item.event_id] || "Evenement"}</p>}
                  </div>
                </div>
                {item.description && <p className="text-xs text-gray-400 line-clamp-2 mb-2">{item.description}</p>}

                {/* YouTube links */}
                {item.youtube_urls?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {item.youtube_urls.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[10px] bg-red-500/10 text-red-400 px-2 py-0.5 rounded-full hover:bg-red-500/20 transition-colors">
                        <Youtube className="h-3 w-3" /> Video {i + 1}
                      </a>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2 border-t border-[#2A3441]">
                  <Button variant="ghost" size="sm" onClick={() => toggleFeatured(item)} className={`text-xs ${item.is_featured ? "text-amber-400" : "text-gray-500"}`} data-testid={`toggle-featured-${item.id}`}>
                    <Star className={`h-3 w-3 mr-1 ${item.is_featured ? "fill-amber-400" : ""}`} /> {item.is_featured ? "En une" : "Vedette"}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => togglePublish(item)} className={`text-xs ${item.status === "published" ? "text-emerald-400" : "text-gray-500"}`} data-testid={`toggle-publish-${item.id}`}>
                    {item.status === "published" ? <><EyeOff className="h-3 w-3 mr-1" /> Depublier</> : <><Eye className="h-3 w-3 mr-1" /> Publier</>}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(item)} className="text-xs text-gray-400" data-testid={`edit-media-${item.id}`}>
                    <Edit3 className="h-3 w-3 mr-1" /> Modifier
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteItem(item.id)} className="text-xs text-red-400" data-testid={`delete-media-${item.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-[#131B2E] border border-[#2A3441] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="media-form-modal">
            <h2 className="text-lg font-bold mb-4">{editItem ? "Modifier le media" : "Nouveau media"}</h2>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Titre</label>
                <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="media-title" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Description</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="w-full bg-[#0B0F19] border border-[#2A3441] text-white rounded-lg p-3 text-sm resize-none h-20" data-testid="media-description" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Categorie</label>
                  <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full bg-[#0B0F19] border border-[#2A3441] text-white rounded-lg p-2.5 text-sm" data-testid="media-category">
                    <option value="conference">Conference</option>
                    <option value="reportage">Reportage</option>
                    <option value="live">Live</option>
                    <option value="evenement">Evenement</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Evenement lie</label>
                  <select value={form.event_id} onChange={e => setForm(f => ({ ...f, event_id: e.target.value }))} className="w-full bg-[#0B0F19] border border-[#2A3441] text-white rounded-lg p-2.5 text-sm" data-testid="media-event">
                    <option value="">Aucun</option>
                    {events.map(ev => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
                  </select>
                </div>
              </div>

              {/* YouTube URLs */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block flex items-center gap-1"><Youtube className="h-3 w-3 text-red-400" /> Liens YouTube</label>
                {form.youtube_urls.map((url, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <Input value={url} onChange={e => updateYoutube(i, e.target.value)} placeholder="https://youtube.com/watch?v=..." className="bg-[#0B0F19] border-[#2A3441] text-white flex-1" data-testid={`youtube-url-${i}`} />
                    {form.youtube_urls.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeYoutube(i)} className="text-red-400 px-2"><X className="h-4 w-4" /></Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addYoutubeField} className="border-[#2A3441] text-gray-400 text-xs"><Plus className="h-3 w-3 mr-1" /> Ajouter lien</Button>
              </div>

              {/* Toggles */}
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.is_featured} onChange={e => setForm(f => ({ ...f, is_featured: e.target.checked }))} className="rounded" data-testid="media-featured" />
                  <Star className="h-3.5 w-3.5 text-amber-400" /> En vedette
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.glotech_production} onChange={e => setForm(f => ({ ...f, glotech_production: e.target.checked }))} className="rounded" data-testid="media-glotech" />
                  <Film className="h-3.5 w-3.5 text-cyan-400" /> Production GLOTECH
                </label>
              </div>

              {/* Status */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Statut</label>
                <div className="flex gap-3">
                  <Button variant={form.status === "draft" ? "default" : "outline"} size="sm" onClick={() => setForm(f => ({ ...f, status: "draft" }))} className={form.status === "draft" ? "bg-gray-600" : "border-[#2A3441] text-gray-400"}>Brouillon</Button>
                  <Button variant={form.status === "published" ? "default" : "outline"} size="sm" onClick={() => setForm(f => ({ ...f, status: "published" }))} className={form.status === "published" ? "bg-emerald-600" : "border-[#2A3441] text-gray-400"}>Publie</Button>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowForm(false)} className="flex-1 border-[#2A3441] text-gray-400">Annuler</Button>
              <Button onClick={saveItem} className="flex-1 bg-[#0099CC] hover:bg-[#00AADD] text-white" data-testid="save-media-btn">
                <Check className="h-4 w-4 mr-1" /> {editItem ? "Enregistrer" : "Creer"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
